import { NodeTypeVisitor, NodeTypeDeterminer } from './node-type';
import { Editor } from './editor';
import { activateEditor, setCursorOnTag, setTextCursor } from './cursor-utils';

export class CursorMoveHorizontally {

  private readonly nodeManager: NodeTypeDeterminer;
  private readonly parentElement: () => Element;

  constructor(nodeManager: NodeTypeDeterminer, parentElement: () => Element) {
    this.nodeManager = nodeManager;
    this.parentElement = parentElement;
  }

  proceedDeletion(toRight: boolean): boolean {
    return this.handleCursor(toRight, (node: Node) => {
      return this.nodeManager.determineType(node, new DeleteProcessor(this.nodeManager, toRight));
    });
  }

  //returns true if set to cell
  moveCursor(toRight: boolean): boolean {
    return this.handleCursor(toRight, (node: Node) => {
      return this.nodeManager.determineType(node, new CursorMover(this.nodeManager, toRight));
    });
  }
  private handleCursor(toRight: boolean, cb: (node: Node) => boolean): boolean {
    const sel = document.getSelection();

    if (sel && sel.type === "Caret") {
      const an = sel.anchorNode;
      if (an) {
        const offset = sel.anchorOffset;
        if (an === this.parentElement()) {
          const children = an.childNodes;
          if (offset < children.length) {
            //cursor before the child
            const right = children.item(offset);
            if (toRight) {
              return cb(right);
            }
            else {
              const left = right.previousSibling;
              if (left) {
                return cb(left);
              }
              else {
                return true;
              }
            }

          }
          else {
            //cursor after the child
            const child = children.item(children.length - 1);
            if (toRight) {
              //the child is last, nothing to do here
              //deny standard handling as well
              return true;
            }
            else {
              //going left into the child
              return cb(child);
            }
          }
        }
        else {
          if (an.nodeType === Node.TEXT_NODE) {
            //cursor could be in any place of the text node
            const t = an as Text;
            if (t.textContent) {
              if (offset === 0 && !toRight) {
                const lb = an.previousSibling;
                if (lb) {
                  return cb(lb);
                }
                else {
                  return true;
                }
              }
              if (offset === t.textContent.length && toRight) {
                //cursor after the text
                const rb = an.nextSibling;
                if (rb) {
                  return cb(rb);
                }
                else {
                  return true;
                }
              }
              return false;
            }
            else {
              //should never be here, but...
              return false;
            }
          }
          else {
            //FIXME: this never occurs!!!, this mean child number offset
            //cursor before the AN. 
            if (toRight) {
              return cb(an);
            }
            else {
              const left = an.previousSibling;
              if (left) {
                return cb(left);
              }
              else {
                return true;
              }
            }
          }

        }
      }
    }
    return false;
  }
}

/**
 * Move cursor into the NODE when cursor moves toRight. Returns false when default browser behavior should be used instead.
 */
class CursorMover implements NodeTypeVisitor<boolean> {

  private readonly td: NodeTypeDeterminer;
  private readonly toRight: boolean;

  constructor(td: NodeTypeDeterminer, toRight: boolean) {
    this.td = td;
    this.toRight = toRight;
  }

  visitServiceBreak(el: HTMLBRElement): boolean {
    //no need to do anything. Skip default behavior
    return true;
  }

  visitCell(id: string, editor: Editor): boolean {
    activateEditor(editor, !this.toRight);
    return true;
  }

  visitText(el: Text): boolean {
    return false;
  }

  visitDelimiter(el: HTMLSpanElement): boolean {
    if (this.toRight) {
      //must go to the NEXT node
      const rb = el.nextSibling;
      if (rb) {
        const self = this;
        this.td.determineType(rb, {
          visitCell(id, editor) {
            activateEditor(editor, !self.toRight);
          },
          visitDelimiter(dl) {
            setCursorOnTag(dl);
          },
          visitLineBreak(lb) {
            setCursorOnTag(lb);
          },
          visitServiceBreak() {
          },
          visitText(t) {
            setTextCursor(t, 0);
          }
        });
      }
    }
    else {
      setCursorOnTag(el);
    }
    return true;
  }

  visitLineBreak(el: HTMLBRElement): boolean {
    if (this.toRight) {
      //must go to the NEXT node
      const rb = el.nextSibling;
      if (rb) {
        const self = this;
        this.td.determineType(rb, {
          visitCell(id, editor) {
            activateEditor(editor, !self.toRight);
          },
          visitDelimiter(dl) {
            setCursorOnTag(dl);
          },
          visitLineBreak(lb) {
            setCursorOnTag(lb);
          },
          visitServiceBreak() {
          },
          visitText(t) {
            setTextCursor(t, 0);
          }
        });
      }
    }
    else {
      setCursorOnTag(el);
    }
    return true;
  }
}

class DeleteProcessor implements NodeTypeVisitor<boolean> {

  private readonly td: NodeTypeDeterminer;
  private readonly toRight: boolean;

  constructor(td: NodeTypeDeterminer, toRight: boolean) {
    this.td = td;
    this.toRight = toRight;
  }

  visitServiceBreak(el: HTMLBRElement): boolean {
    return true;
  }

  visitCell(id: string, editor: Editor): boolean {
    activateEditor(editor, !this.toRight);
    return true;
  }
  visitText(el: Text): boolean {
    setTextCursor(el,this.toRight?0:(el.textContent?el.textContent.length:0))
    return true;
  }
  visitDelimiter(el: HTMLSpanElement): boolean {
    
    if (this.toRight) {
      if (!this.setCursorToRightNb(el)) {
        this.setCursorToLeftNb(el);
      }
    }
    else {
      if (!this.setCursorToLeftNb(el)) {
        this.setCursorToRightNb(el);
      }
    }
    //No need to remove, removing delimiter will cause autoadding it back and setting cursor to it
    //Let onInput decide if the delimiter should be removed
    //el.remove();
    return true;
  }
  visitLineBreak(br: HTMLBRElement): boolean {
    if (this.toRight) {
      if (!this.setCursorToRightNb(br)) {
        this.setCursorToLeftNb(br);
      }
    }
    else {
      if (!this.setCursorToLeftNb(br)) {
        this.setCursorToRightNb(br);
      }
    }

    br.remove();
    return true;
  }

  private setCursorToLeftNb(node: Node): boolean {
    const lb = node.previousSibling;
    if (lb) {
      return this.td.determineType(lb, {
        visitCell(id, editor) {
          activateEditor(editor, true);
          return true;
        },
        visitDelimiter(dl) {
          setCursorOnTag(dl);
          return true;
        },
        visitLineBreak(br) {
          //NO, it would mean jumping through the br
          //setCursorOnTag(br);
          return false;
        },
        visitServiceBreak() {
          return false;
        },
        visitText(t) {
          if (t.textContent) {
            setTextCursor(t, t.textContent.length);
            return true;
          }
          return false;
        }
      });

    }
    return false;
  }

  private setCursorToRightNb(br: Node): boolean {
    const rb = br.nextSibling;
    if (rb) {
      this.td.determineType(rb, {
        visitCell(id, editor) {
          activateEditor(editor, false);
        },
        visitDelimiter(dl) {
          setCursorOnTag(dl);
        },
        visitLineBreak(br) {
          setCursorOnTag(br);
        },
        visitServiceBreak() {
        },
        visitText(t) {
          setTextCursor(t, 0);
        }
      });
      return true;
    }
    return false;
  }

}